// ./src/utils/api/FormFieldTypesService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// GraphQL queries
const FORM_FIELD_TYPE_LIST_QUERY = gql`
  query FormFieldTypeList(
    $filter: FormFieldTypeFilter
    $params: PaginationAndSorting
  ) {
    formFieldTypeList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        description
        iconName
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_TYPE_GET_QUERY = gql`
  query FormFieldTypeGet($id: ID) {
    formFieldTypeGet(id: $id) {
      code
      data {
        id
        name
        description
        iconName
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_TYPE_GET_MANY_QUERY = gql`
  query FormFieldTypeGetMany($ids: [ID]) {
    formFieldTypeGetMany(ids: $ids) {
      code
      data {
        id
        name
        description
        iconName
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface FormFieldType {
  id: string;
  name?: string;
  description?: string;
  iconName?: string;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface FormFieldTypeFilter {
  id?: string[];
  name?: string[];
  status?: number[];
  searchKeyword?: string;
}

/**
 * Form Field Types service that handles all form field type-related API calls
 * Supports read operations: list, get, getMany
 */
export class FormFieldTypesService {
  /**
   * Get a list of form field types with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: FormFieldTypeFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_TYPE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single form field type by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_TYPE_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple form field types by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      FORM_FIELD_TYPE_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }
}
